/** F070 — minimal module-level TTL cache for the shell GETs.
 *
 * The onboarding provider + OperationalChip refire the same 4 GETs on every
 * remount (404 tree ↔ main tree, OAuth/Stripe round trips, login handoff).
 * A tiny promise cache with a short TTL absorbs those repeats; writes
 * invalidate their keys at the service layer, and surfaces that must see
 * server truth (Stripe webhook polling, post-OAuth refresh) pass
 * `fresh: true`.
 *
 * Deliberately NOT a data-fetching lib (react-query/SWR) — 4 keys don't
 * justify the dependency (DECISIONS §20 "boring tech wins"; revisit >8 keys).
 */

interface CacheEntry {
  promise: Promise<unknown>;
  /** null while in flight; stamped on resolution. */
  expiresAt: number | null;
}

const cache = new Map<string, CacheEntry>();

/** Shared TTL for the shell GETs — short enough that stale state is never
 * user-perceptible, long enough to absorb remount bursts. */
export const SHELL_CACHE_TTL_MS = 30_000;

export interface CachedFetchOptions {
  ttlMs: number;
  /** Bypass a warm entry and repopulate the cache with the fresh result. */
  fresh?: boolean;
}

export function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  { ttlMs, fresh = false }: CachedFetchOptions
): Promise<T> {
  if (fresh) cache.delete(key);

  const existing = cache.get(key);
  if (existing && (existing.expiresAt === null || existing.expiresAt > Date.now())) {
    return existing.promise as Promise<T>;
  }

  const entry: CacheEntry = { promise: undefined as never, expiresAt: null };
  entry.promise = fetcher().then(
    (value) => {
      entry.expiresAt = Date.now() + ttlMs;
      return value;
    },
    (err) => {
      // Never cache rejections — the next caller retries.
      if (cache.get(key) === entry) cache.delete(key);
      throw err;
    }
  );
  cache.set(key, entry);
  return entry.promise as Promise<T>;
}

export function invalidate(...keys: string[]): void {
  for (const key of keys) cache.delete(key);
}

export function clearRequestCache(): void {
  cache.clear();
}

// Multi-account hygiene: a 401 anywhere must drop every cached tenant
// response so a subsequent login on the same browser never sees the previous
// account's shell data. AuthContext.logout() also calls clearRequestCache().
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', clearRequestCache);
}
